import { PurchaseOrderSheet } from "@/components/operations";
import { SuccessActionPanel } from "@/components/operations/SuccessActionPanel";
import {
  buildInventoryWorkspacePath,
  formatCurrency,
  formatStatusLabel,
  getPurchaseOrderDisplayStatus,
} from "@/components/operations/operations-shared";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { exportPurchaseOrdersToCsv, exportPurchaseOrdersToExcel } from "@/utils/csv-export";
import type { PurchaseOrder } from "@/core";
import { CalendarIcon, Download, Loader2, X } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  fetchAllPurchaseOrders,
  useAllVendors,
  useCreatePurchaseOrderManual,
  useDeals,
  useGetDealById,
  useGetProducts,
  useInventories,
  usePurchaseOrders,
} from "@/hooks";
import { calculateOrderTotal, getPurchaseOrderTotal } from "@/utils/order-total";
import {
  buildDateRangeConstraints,
  buildExportSuffix,
  formatDateRangeLabel,
  getVendorSearchKeywords,
  getVendorSelectLabel,
} from "@/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

const PAYMENT_TERMS_OPTIONS = [
  { value: "pay_in_advance", label: "Paid in advance" },
  { value: "pay_after", label: "Paid after flight" },
  { value: "pay_once_booked", label: "Pay once booked" },
] as const;

// Payment methods offered when creating a PO. The filter reuses these and adds
// the legacy "paypal" value so older records stay filterable.
const PAYMENT_METHOD_OPTIONS = [
  { value: "paypal_goods_services", label: "PayPal - Goods and Services" },
  { value: "paypal_friends_family", label: "PayPal - Friends and Family" },
  { value: "paypal_goods_services_fees", label: "PayPal - Goods and Services + Fees" },
  { value: "bank", label: "Bank" },
  { value: "zelle", label: "Zelle" },
  { value: "card", label: "Card" },
  { value: "crypto", label: "Crypto" },
  { value: "other", label: "Other" },
] as const;

const PAYMENT_METHOD_FILTER_OPTIONS = [
  ...PAYMENT_METHOD_OPTIONS,
  { value: "paypal", label: "PayPal (legacy)" },
] as const;

const PURCHASE_ORDER_SOURCE_OPTIONS = [
  { value: "CRM", label: "CRM" },
  { value: "Broker", label: "Broker" },
  { value: "Friend", label: "Friend" },
  { value: "Other", label: "Other" },
] as const;

const CREATE_MODE_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "deal", label: "Source deal" },
] as const;

function formatShortId(value: string | undefined) {
  return value?.slice(0, 8) || "Unlinked";
}

function formatDate(value: Date | undefined): string {
  if (!value) {
    return "—";
  }

  return value.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function mapDealPaymentOptionToPoTiming(payload: {
  paymentOption?: string;
}): "pay_in_advance" | "pay_after" | "pay_once_booked" {
  if (payload.paymentOption === "pay_once_booked") {
    return "pay_once_booked";
  }

  if (payload.paymentOption === "pay_after_booking") {
    return "pay_after";
  }

  return "pay_in_advance";
}

const PURCHASE_ORDER_PAGE_SIZE = 100;

/**
 * The payment filters are client-side, so exports must apply them to the fully
 * fetched set — not just to the rows already paged into the table.
 */
function matchesPaymentFilters(
  purchaseOrder: PurchaseOrder,
  filters: { paymentTiming: string; paymentMethod: string },
): boolean {
  if (
    filters.paymentTiming !== "all" &&
    purchaseOrder.paymentTiming !== filters.paymentTiming
  ) {
    return false;
  }

  if (
    filters.paymentMethod !== "all" &&
    purchaseOrder.paymentMethod !== filters.paymentMethod
  ) {
    return false;
  }

  return true;
}

export default function PurchaseOrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPoId = searchParams.get("poId") || "";
  const sourceDealIdFromSearch = searchParams.get("sourceDealId") || "";
  const createModeFromSearch = searchParams.get("createMode");

  const [createdDateRange, setCreatedDateRange] = useState<DateRange | undefined>();
  const createdRangeConstraints = useMemo(
    () => buildDateRangeConstraints<PurchaseOrder>(createdDateRange, "createdAt"),
    [createdDateRange],
  );
  const purchaseOrdersQuery = usePurchaseOrders({
    queryConstraints: createdRangeConstraints,
    pagination: { limit: PURCHASE_ORDER_PAGE_SIZE },
    orderBy: { field: "createdAt", direction: "desc" },
  });
  const inventoriesQuery = useInventories({
    pagination: { limit: 100 },
    orderBy: { field: "updatedAt", direction: "desc" },
  });
  const productsQuery = useGetProducts();
  const vendorsQuery = useAllVendors();
  const sourceDealsQuery = useDeals({
    pagination: { limit: 100 },
    orderBy: { field: "createdAt", direction: "desc" },
  });
  const createMutation = useCreatePurchaseOrderManual();

  const [createMode, setCreateMode] = useState<"manual" | "deal">(
    createModeFromSearch === "deal" || sourceDealIdFromSearch ? "deal" : "manual",
  );
  const [sourceDealId, setSourceDealId] = useState(sourceDealIdFromSearch);
  const selectedSourceDealQuery = useGetDealById(sourceDealId);

  const [vendorId, setVendorId] = useState("");
  const [inventoryMode, setInventoryMode] = useState<"existing" | "new">("existing");
  const [inventoryId, setInventoryId] = useState("");
  const [inventoryName, setInventoryName] = useState("");
  const [accountOwnerName, setAccountOwnerName] = useState("");
  const [accountEmail, setAccountEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [productId, setProductId] = useState("");
  const [productName, setProductName] = useState("");
  const [amountBought, setAmountBought] = useState("");
  const [buyPrice, setBuyPrice] = useState("");
  const [taxCost, setTaxCost] = useState("");
  const [source, setSource] = useState<"CRM" | "Broker" | "Friend" | "Other">("Broker");
  const [paymentTiming, setPaymentTiming] = useState<
    "pay_in_advance" | "pay_after" | "pay_once_booked"
  >("pay_in_advance");
  const [nextPaymentDate, setNextPaymentDate] = useState("");
  const [okToPay, setOkToPay] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<
    | "paypal_goods_services"
    | "paypal_friends_family"
    | "paypal_goods_services_fees"
    | "paypal"
    | "bank"
    | "zelle"
    | "card"
    | "crypto"
    | "other"
  >("paypal_goods_services");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentDetails, setPaymentDetails] = useState("");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [selectedPurchaseOrderIds, setSelectedPurchaseOrderIds] = useState<Set<string>>(
    new Set(),
  );
  const [paymentTimingFilter, setPaymentTimingFilter] = useState<"all" | string>("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<"all" | string>("all");
  const [successState, setSuccessState] = useState<{
    title: string;
    description: string;
    details: string[];
    nextAction: string;
    actions: Array<{ label: string; href?: string; onClick?: () => void }>;
  } | null>(null);

  const purchaseOrders = useMemo(
    () => purchaseOrdersQuery.data?.pages.flat() ?? [],
    [purchaseOrdersQuery.data],
  );
  const filteredPurchaseOrders = useMemo(
    () =>
      purchaseOrders.filter((purchaseOrder) =>
        matchesPaymentFilters(purchaseOrder, {
          paymentTiming: paymentTimingFilter,
          paymentMethod: paymentMethodFilter,
        }),
      ),
    [purchaseOrders, paymentTimingFilter, paymentMethodFilter],
  );
  const hasPaymentFilter =
    paymentTimingFilter !== "all" || paymentMethodFilter !== "all";
  // Selection persists across filter changes, so it spans the full fetched set.
  const selectedPurchaseOrders = useMemo(
    () =>
      purchaseOrders.filter((purchaseOrder) =>
        selectedPurchaseOrderIds.has(purchaseOrder.id),
      ),
    [purchaseOrders, selectedPurchaseOrderIds],
  );
  // Header checkbox + select-all act on the currently visible (filtered) rows.
  const allPurchaseOrdersSelected =
    filteredPurchaseOrders.length > 0 &&
    filteredPurchaseOrders.every((purchaseOrder) =>
      selectedPurchaseOrderIds.has(purchaseOrder.id),
    );
  const somePurchaseOrdersSelected =
    !allPurchaseOrdersSelected &&
    filteredPurchaseOrders.some((purchaseOrder) =>
      selectedPurchaseOrderIds.has(purchaseOrder.id),
    );
  const inventories = useMemo(
    () => inventoriesQuery.data?.pages.flat() ?? [],
    [inventoriesQuery.data],
  );
  const products = productsQuery.data ?? [];
  const vendors = useMemo(
    () => vendorsQuery.data?.pages.flat() ?? [],
    [vendorsQuery.data],
  );
  const sourceDeals = useMemo(() => {
    const deals = sourceDealsQuery.data?.pages.flat() ?? [];
    return deals.filter((deal) => deal.buyOrSell?.toLowerCase() === "sell");
  }, [sourceDealsQuery.data]);

  const selectedSourceDeal = selectedSourceDealQuery.data;
  const availableInventories = useMemo(() => {
    if (createMode !== "deal" || !selectedSourceDeal?.productId) {
      return inventories;
    }

    return inventories.filter(
      (inventory) => inventory.productId === selectedSourceDeal.productId,
    );
  }, [createMode, inventories, selectedSourceDeal?.productId]);
  const selectedInventory = availableInventories.find(
    (inventory) => inventory.id === inventoryId,
  );

  useEffect(() => {
    if (!sourceDealIdFromSearch) {
      return;
    }

    setCreateMode("deal");
    setSourceDealId(sourceDealIdFromSearch);
  }, [sourceDealIdFromSearch]);

  // Prefill only when a different source deal is picked, so refetches of the
  // same deal don't overwrite what the user has typed.
  const prefilledSourceDealIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!selectedSourceDeal) {
      prefilledSourceDealIdRef.current = undefined;
      return;
    }
    if (prefilledSourceDealIdRef.current === selectedSourceDeal.id) {
      return;
    }
    prefilledSourceDealIdRef.current = selectedSourceDeal.id;

    setAmountBought(String(selectedSourceDeal.quantity));
    setBuyPrice(String(selectedSourceDeal.amount));
    setPaymentTiming(
      mapDealPaymentOptionToPoTiming({
        paymentOption: selectedSourceDeal.paymentOption,
      }),
    );
    setInventoryName((currentValue) =>
      currentValue || `${selectedSourceDeal.productName} Inventory`,
    );
    setAccountOwnerName((currentValue) => currentValue || selectedSourceDeal.name);
    setAccountEmail((currentValue) => currentValue || selectedSourceDeal.accountEmail || "");
    setAccountPassword(
      (currentValue) => currentValue || selectedSourceDeal.accountPassword || "",
    );
  }, [selectedSourceDeal]);

  const amountBoughtValue = Number(amountBought);
  const buyPriceValue = Number(buyPrice);
  const taxCostValue = Number(taxCost);
  const totalAmountValue =
    Number.isFinite(buyPriceValue) && Number.isFinite(taxCostValue)
      ? calculateOrderTotal({
        amount: buyPriceValue,
        taxCost: taxCostValue,
      })
      : 0;
  const resolvedProductId =
    createMode === "deal"
      ? selectedSourceDeal?.productId || ""
      : inventoryMode === "existing"
        ? selectedInventory?.productId || ""
        : productId;
  const resolvedProductName =
    createMode === "deal"
      ? selectedSourceDeal?.productName || ""
      : inventoryMode === "existing"
        ? selectedInventory?.productName || ""
        : productName;

  const canCreatePo =
    (createMode === "manual" ? Boolean(vendorId) : Boolean(sourceDealId)) &&
    Boolean(resolvedProductId) &&
    Boolean(resolvedProductName) &&
    (inventoryMode === "existing" ? Boolean(inventoryId) : Boolean(inventoryName.trim())) &&
    Number.isFinite(amountBoughtValue) &&
    amountBoughtValue > 0 &&
    Number.isFinite(buyPriceValue) &&
    buyPriceValue >= 0 &&
    Number.isFinite(taxCostValue) &&
    taxCostValue >= 0;

  const handleSourceDealChange = (nextSourceDealId: string) => {
    setSourceDealId(nextSourceDealId);
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);

      if (nextSourceDealId) {
        next.set("createMode", "deal");
        next.set("sourceDealId", nextSourceDealId);
      } else {
        next.delete("createMode");
        next.delete("sourceDealId");
      }

      return next;
    });
  };

  const handleCreateModeChange = (nextMode: "manual" | "deal") => {
    setCreateMode(nextMode);
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);

      if (nextMode === "deal") {
        next.set("createMode", "deal");
        if (sourceDealId) {
          next.set("sourceDealId", sourceDealId);
        }
      } else {
        next.delete("createMode");
        next.delete("sourceDealId");
      }

      return next;
    });
  };

  const handleCreate = async () => {
    if (!canCreatePo) {
      return;
    }

    const result = await createMutation.mutateAsync(
      createMode === "manual"
        ? {
            createMode: "manual",
            source,
            vendorId,
            productId: resolvedProductId,
            productName: resolvedProductName,
            amountBought: amountBoughtValue,
            buyPrice: buyPriceValue,
            taxCost: taxCostValue,
            paymentTiming,
            nextPaymentDate: nextPaymentDate || undefined,
            okToPay,
            paymentMethod,
            paypalEmail: paypalEmail.trim() || undefined,
            paymentNotes: paymentNotes.trim() || undefined,
            paymentDetails: paymentDetails.trim() || undefined,
            inventoryMode,
            inventoryId: inventoryMode === "existing" ? inventoryId : undefined,
            inventoryName: inventoryMode === "new" ? inventoryName.trim() : undefined,
            accountOwnerName:
              inventoryMode === "new" ? accountOwnerName || undefined : undefined,
            accountEmail: inventoryMode === "new" ? accountEmail || undefined : undefined,
            accountPassword:
              inventoryMode === "new" ? accountPassword || undefined : undefined,
          }
        : {
            createMode: "deal",
            sourceDealId,
            amountBought: amountBoughtValue,
            buyPrice: buyPriceValue,
            taxCost: taxCostValue,
            paymentTiming,
            nextPaymentDate: nextPaymentDate || undefined,
            okToPay,
            paymentMethod,
            paypalEmail: paypalEmail.trim() || undefined,
            paymentNotes: paymentNotes.trim() || undefined,
            paymentDetails: paymentDetails.trim() || undefined,
            inventoryMode,
            inventoryId: inventoryMode === "existing" ? inventoryId : undefined,
            inventoryName: inventoryMode === "new" ? inventoryName.trim() : undefined,
            accountOwnerName:
              inventoryMode === "new" ? accountOwnerName || undefined : undefined,
            accountEmail: inventoryMode === "new" ? accountEmail || undefined : undefined,
            accountPassword:
              inventoryMode === "new" ? accountPassword || undefined : undefined,
          },
    );

    setAmountBought("");
    setBuyPrice("");
    setTaxCost("");
    setInventoryName("");
    setAccountOwnerName("");
    setAccountEmail("");
    setAccountPassword("");
    setSourceDealId("");
    setNextPaymentDate("");
    setOkToPay(false);
    setPaymentMethod("paypal_goods_services");
    setPaypalEmail("");
    setPaymentNotes("");
    setPaymentDetails("");
    setSuccessState({
      title: "Purchase order created",
      description:
        createMode === "deal"
          ? "The purchase order was created from the selected source deal and linked to inventory."
          : "The purchase order is now linked directly to an inventory-backed account.",
      details: [
        `Purchase Order ID: ${result.poId}`,
        `Inventory ID: ${result.inventoryId}`,
        ...(createMode === "deal" ? [`Source Deal ID: ${sourceDealId}`] : []),
      ],
      nextAction:
        "Continue inside the inventory workspace to manage purchase financials and downstream sell orders.",
      actions: [
        {
          label: "Open PO",
          onClick: () => setSearchParams({ poId: result.poId }, { replace: true }),
        },
        {
          label: "Open Inventory Workspace",
          href: buildInventoryWorkspacePath({
            inventoryId: result.inventoryId,
            purchaseOrderId: result.poId,
            focus: "purchase-orders",
          }),
        },
      ],
    });
    setSearchParams({ poId: result.poId }, { replace: true });
  };

  const togglePurchaseOrderSelection = (purchaseOrderId: string, checked: boolean) => {
    setSelectedPurchaseOrderIds((previous) => {
      const next = new Set(previous);
      if (checked) {
        next.add(purchaseOrderId);
      } else {
        next.delete(purchaseOrderId);
      }
      return next;
    });
  };

  const [exportPending, setExportPending] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);

  /**
   * Exports every purchase order in the selected date window — not just the rows
   * already paged into the table. Payment filters still apply.
   */
  const handleExportRange = async (fileFormat: "csv" | "excel") => {
    setExportPending(true);
    setExportMessage(null);

    try {
      const fetched = await fetchAllPurchaseOrders(createdRangeConstraints);
      const orders = fetched.filter((purchaseOrder) =>
        matchesPaymentFilters(purchaseOrder, {
          paymentTiming: paymentTimingFilter,
          paymentMethod: paymentMethodFilter,
        }),
      );

      if (orders.length === 0) {
        setExportMessage(
          "No purchase orders found for the selected date range and filters.",
        );
        return;
      }

      const suffix = buildExportSuffix(createdDateRange);

      if (fileFormat === "csv") {
        exportPurchaseOrdersToCsv(orders, `purchase-orders-${suffix}.csv`);
      } else {
        exportPurchaseOrdersToExcel(orders, `purchase-orders-${suffix}.xlsx`);
      }

      setExportMessage(
        `Exported ${orders.length.toLocaleString()} purchase order${orders.length === 1 ? "" : "s"} (${formatDateRangeLabel(createdDateRange)}${hasPaymentFilter ? ", payment filters applied" : ""}).`,
      );
    } catch (error) {
      setExportMessage(
        error instanceof Error
          ? `Export failed: ${error.message}`
          : "Export failed. Please try again.",
      );
    } finally {
      setExportPending(false);
    }
  };

  const handleExportSelected = (fileFormat: "csv" | "excel") => {
    const suffix = buildExportSuffix(createdDateRange);

    if (fileFormat === "csv") {
      exportPurchaseOrdersToCsv(
        selectedPurchaseOrders,
        `purchase-orders-selected-${suffix}.csv`,
      );
    } else {
      exportPurchaseOrdersToExcel(
        selectedPurchaseOrders,
        `purchase-orders-selected-${suffix}.xlsx`,
      );
    }
  };

  const toggleAllPurchaseOrdersSelection = (checked: boolean) => {
    setSelectedPurchaseOrderIds((previous) => {
      const next = new Set(previous);

      filteredPurchaseOrders.forEach((purchaseOrder) => {
        if (checked) {
          next.add(purchaseOrder.id);
        } else {
          next.delete(purchaseOrder.id);
        }
      });

      return next;
    });
  };

  return (
    <div className="container mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
      {successState ? (
        <SuccessActionPanel
          title={successState.title}
          description={successState.description}
          details={successState.details}
          nextAction={successState.nextAction}
          actions={successState.actions}
          onDismiss={() => setSuccessState(null)}
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Create Purchase Order</CardTitle>
          <CardDescription>
            Create a purchase order manually or directly from a source deal. New purchase
            orders stay inventory-linked in the redesigned operations flow.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="space-y-1">
              <Label>Create Mode</Label>
              <Select
                value={createMode}
                onValueChange={(value: "manual" | "deal") =>
                  handleCreateModeChange(value)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CREATE_MODE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {createMode === "deal" ? (
              <div className="space-y-1 md:col-span-2 xl:col-span-3">
                <Label>Source Deal</Label>
                <SearchableSelect
                  value={sourceDealId}
                  onChange={handleSourceDealChange}
                  placeholder="Select source deal"
                  searchPlaceholder="Search source deal..."
                  emptyText="No matching source deals found."
                  options={sourceDeals.map((deal) => ({
                    value: deal.id,
                    label: `${deal.name} · ${deal.productName} · ${deal.quantity.toLocaleString()} miles`,
                    keywords: [deal.name, deal.productName, deal.id],
                  }))}
                />
              </div>
            ) : (
              <div className="space-y-1 md:col-span-2 xl:col-span-3">
                <Label>Vendor</Label>
                <SearchableSelect
                  value={vendorId}
                  onChange={setVendorId}
                  placeholder="Select vendor"
                  searchPlaceholder="Search vendor..."
                  emptyText="No vendor found."
                  options={vendors.map((vendor) => ({
                    value: vendor.id,
                    label: getVendorSelectLabel({ vendor }),
                    keywords: getVendorSearchKeywords({ vendor }),
                  }))}
                />
              </div>
            )}

            <div className="space-y-1">
              <Label>Inventory Mode</Label>
              <Select
                value={inventoryMode}
                onValueChange={(value: "existing" | "new") => setInventoryMode(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="existing">Use existing inventory</SelectItem>
                  <SelectItem value="new">Create new inventory</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {createMode === "deal" && sourceDealId ? (
              <div className="rounded-lg border bg-muted/20 p-4 md:col-span-2 xl:col-span-4">
                {selectedSourceDealQuery.isLoading ? (
                  <div className="text-sm text-muted-foreground">
                    Loading source deal context...
                  </div>
                ) : selectedSourceDeal ? (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Deal
                      </div>
                      <div className="mt-1 text-sm font-medium">
                        {selectedSourceDeal.name}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Product
                      </div>
                      <div className="mt-1 text-sm font-medium">
                        {selectedSourceDeal.productName}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Quantity
                      </div>
                      <div className="mt-1 text-sm font-medium">
                        {selectedSourceDeal.quantity.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Amount
                      </div>
                      <div className="mt-1 text-sm font-medium">
                        ${selectedSourceDeal.amount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-destructive">
                    The selected source deal could not be loaded.
                  </div>
                )}
              </div>
            ) : null}

            {inventoryMode === "existing" ? (
              <div className="space-y-1">
                <Label>Inventory</Label>
                <SearchableSelect
                  value={inventoryId}
                  onChange={setInventoryId}
                  placeholder="Select inventory"
                  searchPlaceholder="Search inventory..."
                  emptyText="No inventories found."
                  options={availableInventories.map((inventory) => ({
                    value: inventory.id,
                    label: `${inventory.name} · ${inventory.productName}`,
                  }))}
                />
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <Label>Inventory Name</Label>
                  <Input
                    value={inventoryName}
                    onChange={(event) => setInventoryName(event.target.value)}
                    placeholder="AA Summer inventory"
                  />
                </div>

                {createMode === "manual" ? (
                  <div className="space-y-1">
                    <Label>Product</Label>
                    <SearchableSelect
                      value={productId}
                      onChange={(selectedId) => {
                        setProductId(selectedId);
                        const selectedProduct = products.find(
                          (product) => product.id === selectedId,
                        );
                        setProductName(selectedProduct?.name || "");
                      }}
                      placeholder="Select product"
                      searchPlaceholder="Search product..."
                      emptyText="No product found."
                      options={products.map((product) => ({
                        value: product.id,
                        label: product.name,
                      }))}
                    />
                  </div>
                ) : (
                  <div className="space-y-1">
                    <Label>Product</Label>
                    <Input value={resolvedProductName} disabled />
                  </div>
                )}

                <div className="space-y-1">
                  <Label>Account Owner</Label>
                  <Input
                    value={accountOwnerName}
                    onChange={(event) => setAccountOwnerName(event.target.value)}
                    placeholder="Operator or vendor name"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Account Email</Label>
                  <Input
                    value={accountEmail}
                    onChange={(event) => setAccountEmail(event.target.value)}
                    placeholder="optional@email.com"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Account Password</Label>
                  <Input
                    type="password"
                    value={accountPassword}
                    onChange={(event) => setAccountPassword(event.target.value)}
                    placeholder="Optional"
                  />
                </div>
              </>
            )}

            <div className="space-y-1">
              <Label>Amount Bought</Label>
              <Input
                value={amountBought}
                onChange={(event) => setAmountBought(event.target.value)}
                placeholder="30000"
              />
            </div>

            <div className="space-y-1">
              <Label>Buy Price</Label>
              <Input
                value={buyPrice}
                onChange={(event) => setBuyPrice(event.target.value)}
                placeholder="450"
              />
            </div>

            <div className="space-y-1">
              <Label>Tax Cost</Label>
              <Input
                value={taxCost}
                onChange={(event) => setTaxCost(event.target.value)}
                placeholder="0"
              />
            </div>

            {createMode === "manual" ? (
              <div className="space-y-1">
                <Label>Source</Label>
                <Select
                  value={source}
                  onValueChange={(value) => setSource(value as typeof source)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PURCHASE_ORDER_SOURCE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1">
                <Label>Source</Label>
                <Input value="CRM / source deal" disabled />
              </div>
            )}

            <div className="space-y-1">
              <Label>Payment Terms</Label>
              <Select
                value={paymentTiming}
                onValueChange={(value) =>
                  setPaymentTiming(value as typeof paymentTiming)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_TERMS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Next Payment Date</Label>
              <Input
                type="date"
                value={nextPaymentDate}
                onChange={(event) => setNextPaymentDate(event.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label>Payment Method</Label>
              <Select
                value={paymentMethod}
                onValueChange={(value) => setPaymentMethod(value as typeof paymentMethod)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paypal_goods_services">PayPal - Goods and Services</SelectItem>
                  <SelectItem value="paypal_friends_family">PayPal - Friends and Family</SelectItem>
                  <SelectItem value="paypal_goods_services_fees">PayPal - Goods and Services + Fees</SelectItem>
                  <SelectItem value="bank">Bank</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="crypto">Crypto</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>PayPal Email</Label>
              <Input
                type="email"
                value={paypalEmail}
                onChange={(event) => setPaypalEmail(event.target.value)}
                placeholder="Optional PayPal email"
              />
            </div>

            <div className="space-y-1">
              <Label>OK To Pay</Label>
              <Select
                value={okToPay ? "yes" : "no"}
                onValueChange={(value) => setOkToPay(value === "yes")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1 md:col-span-2 xl:col-span-4">
              <Label>Payment Notes</Label>
              <Textarea
                value={paymentNotes}
                onChange={(event) => setPaymentNotes(event.target.value)}
                placeholder="Optional payment notes"
              />
            </div>

            <div className="space-y-1 md:col-span-2 xl:col-span-4">
              <Label>Payment Details</Label>
              <Textarea
                value={paymentDetails}
                onChange={(event) => setPaymentDetails(event.target.value)}
                placeholder="Optional payment details"
              />
            </div>
          </div>

          <div className="rounded-lg border bg-muted/20 px-4 py-3 text-sm">
            Total Amount: {formatCurrency({ value: totalAmountValue })}
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleCreate}
              disabled={!canCreatePo}
              loading={createMutation.isPending}
              loadingText="Creating..."
            >
              {createMode === "deal"
                ? "Create Purchase Order from Source Deal"
                : "Create Purchase Order"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Purchase Orders</CardTitle>
            <CardDescription>
              Purchase orders remain list views and route into the linked inventory
              workspace. Pick a created-date range to narrow the view — exports always
              cover the whole range, not just the rows loaded below.
            </CardDescription>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={exportPending}>
                {exportPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                {exportPending ? "Exporting..." : "Export"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={selectedPurchaseOrders.length === 0}
                onClick={() => handleExportSelected("csv")}
              >
                Export Selected CSV ({selectedPurchaseOrders.length})
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={selectedPurchaseOrders.length === 0}
                onClick={() => handleExportSelected("excel")}
              >
                Export Selected Excel ({selectedPurchaseOrders.length})
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportRange("csv")}>
                {createdDateRange?.from
                  ? "Export CSV — all in date range"
                  : "Export CSV — all purchase orders"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportRange("excel")}>
                {createdDateRange?.from
                  ? "Export Excel — all in date range"
                  : "Export Excel — all purchase orders"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <div className="space-y-1">
              <Label>Created Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full min-w-0 justify-start overflow-hidden text-left font-normal sm:w-64"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {formatDateRangeLabel(createdDateRange)}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={createdDateRange}
                    onSelect={setCreatedDateRange}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-1">
              <Label>Payment Terms</Label>
              <Select
                value={paymentTimingFilter}
                onValueChange={setPaymentTimingFilter}
              >
                <SelectTrigger className="sm:w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All payment terms</SelectItem>
                  {PAYMENT_TERMS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Payment Method</Label>
              <Select
                value={paymentMethodFilter}
                onValueChange={setPaymentMethodFilter}
              >
                <SelectTrigger className="sm:w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All payment methods</SelectItem>
                  {PAYMENT_METHOD_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {hasPaymentFilter || createdDateRange?.from ? (
              <div className="flex items-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPaymentTimingFilter("all");
                    setPaymentMethodFilter("all");
                    setCreatedDateRange(undefined);
                  }}
                >
                  <X className="mr-2 h-4 w-4" />
                  Clear filters
                </Button>
              </div>
            ) : null}
          </div>

          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>
              Showing {filteredPurchaseOrders.length.toLocaleString()} purchase order
              {filteredPurchaseOrders.length === 1 ? "" : "s"}
              {purchaseOrdersQuery.hasNextPage ? " (more available)" : ""} ·{" "}
              {formatDateRangeLabel(createdDateRange)}
              {selectedPurchaseOrders.length > 0
                ? ` · ${selectedPurchaseOrders.length.toLocaleString()} selected`
                : ""}
            </span>
            {exportMessage ? (
              <span className="text-foreground">{exportMessage}</span>
            ) : null}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    aria-label="Select all purchase orders"
                    checked={
                      allPurchaseOrdersSelected
                        ? true
                        : somePurchaseOrdersSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(checked) =>
                      toggleAllPurchaseOrdersSelection(checked === true)
                    }
                    onClick={(event) => event.stopPropagation()}
                  />
                </TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Inventory</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead className="text-right">Miles</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {purchaseOrdersQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">
                    Loading purchase orders...
                  </TableCell>
                </TableRow>
              ) : filteredPurchaseOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-muted-foreground">
                    {hasPaymentFilter
                      ? "No purchase orders match the selected filters."
                      : "No purchase orders found."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPurchaseOrders.map((purchaseOrder) => (
                  <TableRow
                    key={purchaseOrder.id}
                    className="cursor-pointer"
                    onClick={() => setSearchParams({ poId: purchaseOrder.id }, { replace: true })}
                  >
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        aria-label={`Select purchase order ${purchaseOrder.id.slice(0, 8)}`}
                        checked={selectedPurchaseOrderIds.has(purchaseOrder.id)}
                        onCheckedChange={(checked) =>
                          togglePurchaseOrderSelection(
                            purchaseOrder.id,
                            checked === true,
                          )
                        }
                      />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(purchaseOrder.createdAt)}
                    </TableCell>
                    <TableCell>{formatShortId(purchaseOrder.inventoryId)}</TableCell>
                    <TableCell>
                      {purchaseOrder.vendorDisplayName ||
                        purchaseOrder.vendorEmail ||
                        "Unknown vendor"}
                    </TableCell>
                    <TableCell>{formatStatusLabel(getPurchaseOrderDisplayStatus(purchaseOrder))}</TableCell>
                    <TableCell>{formatStatusLabel(purchaseOrder.paymentStatus)}</TableCell>
                    <TableCell className="text-right">
                      {purchaseOrder.amountBought.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      ${getPurchaseOrderTotal({
                        buyPrice: purchaseOrder.buyPrice,
                        taxCost: purchaseOrder.taxCost,
                        totalAmount: purchaseOrder.totalAmount,
                      }).toLocaleString()}
                    </TableCell>
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <Button asChild variant="ghost" size="sm">
                        <Link
                          to={buildInventoryWorkspacePath({
                            inventoryId: purchaseOrder.inventoryId,
                            purchaseOrderId: purchaseOrder.id,
                            focus: "purchase-orders",
                          })}
                        >
                          Workspace
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {purchaseOrdersQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => purchaseOrdersQuery.fetchNextPage()}
                loading={purchaseOrdersQuery.isFetchingNextPage}
                loadingText="Loading..."
              >
                Load more
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <PurchaseOrderSheet
        poId={selectedPoId}
        open={selectedPoId.length > 0}
        onOpenChange={(open) => {
          if (!open) {
            setSearchParams((previous) => {
              const next = new URLSearchParams(previous);
              next.delete("poId");
              return next;
            }, { replace: true });
          }
        }}
      />
    </div>
  );
}
